import { useCallback, useEffect, useState } from 'react';

/**
 * The live content width of an element (0 until measured, and in environments
 * without ResizeObserver). Returns a callback ref to attach.
 */
export const useElementWidth = <T extends HTMLElement>() => {
  const [element, setElement] = useState<T | null>(null);
  const [width, setWidth] = useState(0);
  const ref = useCallback((node: T | null) => setElement(node), []);

  useEffect(() => {
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const next = Math.round(entries[0]?.contentRect.width ?? 0);
      setWidth((current) => (current === next ? current : next));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return [ref, width] as const;
};

/**
 * Keeps `--org-fit-height` on `element` equal to the room between its top edge
 * and the bottom of the window (less `gap`), so a sticky panel's footer stays
 * on screen whether or not the page has scrolled.
 */
export const useFitToViewport = (element: HTMLElement | null, gap = 16) => {
  useEffect(() => {
    if (!element) return undefined;
    let frame = 0;
    const update = () => {
      frame = 0;
      const top = Math.max(0, element.getBoundingClientRect().top);
      element.style.setProperty('--org-fit-height', `${Math.max(240, Math.round(window.innerHeight - top - gap))}px`);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    // Scroll events don't bubble; capture catches the app's scrolling <main>.
    document.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      document.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
    };
  }, [element, gap]);
};
